import { memo, useMemo } from 'react'
import type { Components } from 'react-markdown'
import { twMerge } from 'tailwind-merge'
import { Markdown, type MarkdownProps } from '@highlight/ui'
import { createFlowingBlockComponents } from '@renderer/chat/components/flowing-block-components'
import { closeOpenInlineMarkers, revealBoundary, splitMarkdownBlocks } from './streaming-markdown'

/** How long each streamed word takes to fade in. */
export const SUMMARY_FADE_MS = 200

/** How a summary reveals as it streams in, for `useSmoothStreamingText`. */
export const SUMMARY_REVEAL = { boundary: revealBoundary, settleMs: SUMMARY_FADE_MS }

interface SummaryMarkdownProps {
  summary: string
  isPending: boolean
  className?: string
  size?: MarkdownProps['size']
  components?: Components
  urlTransform?: (url: string) => string | null
}

export function SummaryMarkdown({
  summary,
  isPending,
  className,
  size,
  components,
  urlTransform,
}: SummaryMarkdownProps) {
  const markdownComponents = useMemo(() => {
    const flowingComponents: Components = {
      ...createFlowingBlockComponents({
        shouldAnimate: isPending,
        flowStaticText: true,
        // One word per fade: a chunk that grows after it has faded in shows its new words at once.
        wordsPerChunk: 1,
        fadeDuration: SUMMARY_FADE_MS,
      }),
      ...components,
    }
    return flowingComponents
  }, [isPending, components])

  const blocks = useMemo(() => {
    if (!isPending) return null
    const split = splitMarkdownBlocks(summary)
    if (split.length > 0) split[split.length - 1] = closeOpenInlineMarkers(split[split.length - 1])
    return split
  }, [isPending, summary])

  const rootClassName = twMerge('transition-[margin]', className)

  if (blocks) {
    return (
      <Markdown.Prose size={size} className={rootClassName}>
        {blocks.map((block, index) => (
          <SummaryBlock
            // Completed blocks keep their index as the stream grows; only the tail changes.
            key={index}
            source={block}
            components={markdownComponents}
            urlTransform={urlTransform}
          />
        ))}
      </Markdown.Prose>
    )
  }

  return (
    <Markdown size={size} components={markdownComponents} urlTransform={urlTransform} className={rootClassName}>
      {summary}
    </Markdown>
  )
}

interface SummaryBlockProps {
  source: string
  components: Components
  urlTransform?: (url: string) => string | null
}

/**
 * One top-level block of a streaming summary. Memoized on its source, so a chunk that only grows
 * the tail leaves every completed block unparsed and untouched, and its already-faded word chunks
 * keep their state.
 */
const SummaryBlock = memo(function SummaryBlock({ source, components, urlTransform }: SummaryBlockProps) {
  return (
    <Markdown bare components={components} urlTransform={urlTransform}>
      {source}
    </Markdown>
  )
})
